import { EditOutlined } from '@ant-design/icons'
import { useHasAccess } from '@components/HasAccess'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { Permission } from '@utils/permissions'
import { App, Button, Card, Flex, Input, Space, Spin, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'

const { Text } = Typography

interface Props {
  entityId: number
  fetchNote: () => Promise<string | null>
  saveNote: (note: string) => Promise<unknown>
}

const InternalNoteCard = ({ entityId, fetchNote, saveNote }: Props) => {
  const { message } = App.useApp()
  const { hasAccess } = useHasAccess()

  const canRead = hasAccess(Permission.readUser)
  const canEdit = hasAccess(Permission.updateUser)

  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const [note, setNote] = useState<string>('')
  const [draft, setDraft] = useState<string>('')

  useEffect(() => {
    if (!canRead) {
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)
    fetchNote()
      .then((value) => {
        if (active) setNote(value ?? '')
      })
      .catch(() => {
        if (active) message.error(DEFAULT_ERROR)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
    // Re-fetch when the fiche changes; fetchNote closes over entityId.
  }, [entityId, canRead])

  if (!canRead) {
    return null
  }

  const startEditing = () => {
    setDraft(note)
    setEditing(true)
  }

  const handleSave = () => {
    setSaving(true)
    saveNote(draft)
      .then(() => {
        setNote(draft)
        setEditing(false)
        message.success(DEFAULT_SUCCESS)
      })
      .catch(() => message.error(DEFAULT_ERROR))
      .finally(() => setSaving(false))
  }

  return (
    <Space orientation="vertical" className="w-full" size={10}>
      <PageTitle
        level={5}
        title={'Internal note'}
        style={{ margin: 0 }}
        toolbar={
          canEdit && !editing && !loading ? (
            <Button icon={<EditOutlined />} onClick={startEditing} />
          ) : undefined
        }
      />
      <Card>
        {loading ? (
          <Spin />
        ) : editing ? (
          <Flex vertical gap={12}>
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoSize={{ minRows: 3 }}
              disabled={saving}
            />
            <Flex justify="end" gap={8}>
              <Button onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleSave} loading={saving}>
                Save
              </Button>
            </Flex>
          </Flex>
        ) : note ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{note}</Text>
        ) : (
          <Text type="secondary">Aucune note interne</Text>
        )}
      </Card>
    </Space>
  )
}

export default InternalNoteCard
